// Service Records PR 20 — Role-based home routing.
//
// Returns the right landing path for a user given their employee
// role. Sign-in flow + the bare "/" route consume this helper so
// every user lands on a screen tuned to their actual day.
//
// Routing table:
//
//   system_admin   → /dashboard          (CEO Cockpit — Ops merged in
//                                          2026-06-21; sprint + automation
//                                          health + admin tools now live on
//                                          the dashboard)
//   compliance     → /dashboard          (same; compliance officers
//                                          monitor automation health,
//                                          import status, etc.)
//   advisor        → /my-day             (My Day — net-new)
//   company_admin  → /my-day             (founders/managers who also
//                                          carry a personal pipeline)
//   front_desk     → /dashboard/donna    (existing Donna dashboard)
//   back_office    → /dashboard          (existing founder dashboard
//                                          fallback)
//   null/unknown   → /dashboard          (safe default)
//
// The exact destinations are wired in App.tsx routes. This helper
// only emits paths; rendering happens via React Router redirects.

import type { RoleAwareUser } from "./role-visibility";

export const ROLE_HOME_PATHS = {
  // 2026-06-21: Ops Dashboard merged into the CEO Cockpit. system_admin
  // (Grant + Baker) now lands on /dashboard, where the merged dashboard
  // surfaces the pre-cutover sprint, automation health, and admin tools.
  system_admin: "/dashboard",
  // 2026-06-05 (PR D): operational roles now land on /dashboard, where
  // the per-person dispatch routes them to their role-dashboard
  // archetype (Briefing for relationship advisors, Cockpit for service /
  // origination). Was /my-day (a generic card launchpad) / /admin/ops.
  // /my-day is still reachable via the sidebar "Today" shortcut.
  compliance: "/dashboard",
  advisor: "/dashboard",
  company_admin: "/dashboard",
  // Donna's dashboard route is /dashboard/donna — there is no bare
  // /donna route, so the old value fell through to the catch-all "*"
  // → "/" → back here → infinite redirect loop. Fixed 2026-06-04.
  front_desk: "/dashboard/donna",
  back_office: "/dashboard",
  // Ambassador Agents (deny-by-default role, migration 0241) land on
  // their one working surface — the referral page. Sending them to
  // /dashboard would render a shell of 403s.
  ambassador: "/referrals",
  default: "/dashboard",
} as const;

/**
 * Returns the home path for a given role.
 *
 * @param user The session user (or null for signed-out).
 * @returns Absolute path string starting with "/".
 */
export function roleHomePath(user: RoleAwareUser): string {
  const slug = user?.employee_role_slug ?? null;
  if (slug === "system_admin") return ROLE_HOME_PATHS.system_admin;
  if (slug === "compliance") return ROLE_HOME_PATHS.compliance;
  if (slug === "advisor") return ROLE_HOME_PATHS.advisor;
  if (slug === "company_admin") return ROLE_HOME_PATHS.company_admin;
  if (slug === "front_desk") return ROLE_HOME_PATHS.front_desk;
  if (slug === "back_office") return ROLE_HOME_PATHS.back_office;
  if (slug === "ambassador") return ROLE_HOME_PATHS.ambassador;
  return ROLE_HOME_PATHS.default;
}
